(() => {
  const KEY = 'aurholm_style_preview';
  const MAX_ITEMS = 3;
  const EXPIRY_DAYS = 7;
  const PREVIEW_PAGE = '/pages/free-style-preview';

  const expiresAt = () => Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  const isPreviewPage = () => window.location.pathname.includes(PREVIEW_PAGE);

  const isProductPage = () => {
    return document.body.classList.contains('template-product') || window.location.pathname.includes('/products/');
  };

  const parseJson = (value, fallback) => {
    try {
      return JSON.parse(value || '');
    } catch {
      return fallback;
    }
  };

  const readStore = () => {
    const stored = parseJson(localStorage.getItem(KEY), null);
    if (!stored || !Array.isArray(stored.items)) return [];

    if (stored.expires_at && stored.expires_at < Date.now()) {
      localStorage.removeItem(KEY);
      return [];
    }

    return stored.items;
  };

  const writeStore = (items) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        items,
        expires_at: expiresAt(),
      })
    );
  };

  const selectedOptionsObject = (names, values) => {
    return names.reduce((result, name, index) => {
      result[name] = values[index] || '';
      return result;
    }, {});
  };

  const getCurrentVariant = (button) => {
    if (button.dataset.useCurrentVariant !== 'true') return null;

    const productInfo = button.closest('product-info') || document;
    const variantInput = productInfo.querySelector('.product-variant-id');
    const variants = parseJson(button.getAttribute('data-style-preview-variants'), []);

    return variants.find((variant) => String(variant.id) === String(variantInput?.value)) || null;
  };

  const buildItem = (button) => {
    const optionNames = parseJson(button.getAttribute('data-option-names'), []);
    let variantOptions = parseJson(button.getAttribute('data-variant-options'), []);
    let variantId = button.dataset.variantId || '';
    let variantTitle = button.dataset.variantTitle || '';
    let variantSku = button.dataset.variantSku || '';

    const currentVariant = getCurrentVariant(button);
    if (currentVariant) {
      variantId = currentVariant.id || variantId;
      variantTitle = currentVariant.title || variantTitle;
      variantOptions = currentVariant.options || variantOptions;
      variantSku = currentVariant.sku || variantSku;
    }

    return {
      product_title: button.dataset.productTitle || '',
      product_url: button.dataset.productUrl || '',
      product_handle: button.dataset.productHandle || '',
      product_id: button.dataset.productId || '',
      variant_id: variantId || null,
      variant_title: variantTitle || null,
      variant_sku: variantSku || null,
      selected_options: selectedOptionsObject(optionNames, variantOptions),
      product_image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
      source_page: window.location.href,
      source_context: button.dataset.sourceContext || '',
      selected_at: new Date().toISOString(),
    };
  };

  const pushEvent = (eventName, item) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      product_id: item?.product_id,
      product_handle: item?.product_handle,
      variant_id: item?.variant_id,
      page_url: window.location.href,
    });
  };

  const showMessage = (text) => {
    const message = document.querySelector('[data-style-preview-message]');
    if (!message) return;

    message.textContent = text;
    message.hidden = false;

    window.clearTimeout(showMessage.timer);
    showMessage.timer = window.setTimeout(() => {
      message.hidden = true;
    }, 2600);
  };

  const isSelected = (productId) => {
    return readStore().some((item) => String(item.product_id) === String(productId));
  };

  const updateButtons = () => {
    document.querySelectorAll('[data-style-preview-add]').forEach((button) => {
      const added = isSelected(button.dataset.productId);
      const label = button.querySelector('[data-style-preview-label]');

      button.classList.toggle('is-added', added);
      button.setAttribute('aria-pressed', added ? 'true' : 'false');
      if (label) {
        const labelDefault = button.dataset.labelDefault || 'Add to Preview';
        const labelAdded = button.dataset.labelAdded || 'Added';
        label.textContent = added ? labelAdded : labelDefault;
      }
    });

    document.querySelectorAll('[data-style-preview-product-link]').forEach((link) => {
      link.hidden = readStore().length === 0;
    });
  };

  const thumbMarkup = (item) => {
    const image = item.product_image
      ? `<img src="${item.product_image}" alt="${item.product_title}">`
      : '';

    return `
      <span class="style-preview-thumb">
        ${image}
        <button type="button" data-style-preview-remove="${item.product_id}" aria-label="Remove ${item.product_title}">&times;</button>
      </span>
    `;
  };

  const updateBar = () => {
    const bar = document.getElementById('StylePreviewBar');

    // Hidden on product pages (the FSP card has its own summary) and on the
    // preview page itself (the form shows the saved styles; the bar would just
    // duplicate a CTA to the page the visitor is already on).
    if (isProductPage() || isPreviewPage()) {
      if (bar) bar.hidden = true;
      return;
    }

    const items = readStore();
    const count = document.querySelector('[data-style-preview-count]');
    const thumbs = document.querySelector('[data-style-preview-thumbs]');

    if (!bar || !count || !thumbs) return;

    bar.hidden = items.length === 0;
    count.textContent = `${items.length} ${items.length === 1 ? 'style' : 'styles'} selected`;
    thumbs.innerHTML = items.map(thumbMarkup).join('');
  };

  // Inline "selected styles" summary inside the product-page FSP card.
  const updateProductSummary = () => {
    const summary = document.querySelector('[data-style-preview-product-summary]');
    if (!summary) return;

    const items = readStore();
    const count = summary.querySelector('[data-style-preview-product-count]');
    const thumbs = summary.querySelector('[data-style-preview-product-thumbs]');

    summary.hidden = items.length === 0;
    if (count) count.textContent = items.length;
    if (thumbs) thumbs.innerHTML = items.map(thumbMarkup).join('');
  };

  const refresh = () => {
    updateButtons();
    updateBar();
    updateProductSummary();
  };

  const addOrRemove = (button) => {
    const item = buildItem(button);
    let items = readStore();

    if (items.some((selected) => String(selected.product_id) === String(item.product_id))) {
      items = items.filter((selected) => String(selected.product_id) !== String(item.product_id));
      writeStore(items);
      pushEvent('style_preview_remove', item);
      refresh();
      return;
    }

    if (items.length >= MAX_ITEMS) {
      showMessage('You can preview up to 3 styles. Remove one to add another.');
      pushEvent('style_preview_limit_reached', item);
      return;
    }

    items.push(item);
    writeStore(items);
    pushEvent('style_preview_add', item);
    refresh();
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-style-preview-add]');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      addOrRemove(addButton);
      return;
    }

    const removeButton = event.target.closest('[data-style-preview-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();

      const productId = removeButton.dataset.stylePreviewRemove;
      const item = readStore().find((selected) => String(selected.product_id) === String(productId));

      writeStore(readStore().filter((selected) => String(selected.product_id) !== String(productId)));
      pushEvent('style_preview_remove', item);
      refresh();
      return;
    }

    if (event.target.closest('[data-style-preview-clear]')) {
      event.preventDefault();
      writeStore([]);
      refresh();
      return;
    }

    if (event.target.closest('[data-style-preview-open]')) {
      pushEvent('style_preview_open', null);
    }
  });

  document.addEventListener('DOMContentLoaded', refresh);
})();
